import React, { useEffect, useRef, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/Add';
import {
    gridFilteredSortedRowIdsSelector,
    gridSortedRowIdsSelector,
    GridToolbarColumnsButton, // keep these for now. Switching to a custom toolbar is more involved.
    GridToolbarContainer, GridToolbarDensitySelector,
    GridToolbarFilterButton,
    useGridApiContext,
} from '@mui/x-data-grid';
import {
    Box, Checkbox, createSvgIcon, IconButton, Popover, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import FileCopyIcon from '@mui/icons-material/FileCopy';

import { IndigoSvg } from 'BMapsSrc/IndigoSvg';
import BMapsTooltip from '../BMapsTooltip';
import { getNumberedCompoundName } from '../dialog/ScreeningPane/Tabs/ScreeningResults';
import { EditInPlaceInput, HoverableCompoundStructureCell } from './TableCells';
import { TextArea } from '../common/TextFields';
import CopyToClipboardButton from '../common/Buttons/CopyToClipboardButton';

export function ScoringCoralToolbar({
    toolBarId, charts, setChart, compounds, addCompound, exportOptions,
}) {
    const compoundOptions = compounds.map(
        (comp) => ({ title: comp.resSpec, onClick: () => addCompound(comp) })
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            padding: '4px 4px 0px',
            alignItems: 'center',
            border: '1px solid lightgray',
        }}
        >
            <AddCompoundButton toolBarId={toolBarId} compoundOptions={compoundOptions} />
            {!!exportOptions && (
                <ExportButton toolBarId={toolBarId} exportOptions={exportOptions} />
            )}
            <ChartButton toolBarId={toolBarId} charts={charts} setActiveChart={setChart} />
        </Box>
    );
}

/**
 * @param {{
 * toolBarId: string,
 * charts: string[],
 * setChart: (chart: string) => void,
 * exportOptions: {}
 * }} param0
 * @returns
 */
export function ScreeningCoralToolBar({
    toolBarId, charts, setChart, exportOptions,
    screeningCompoundInfoList, updateScreeningCompoundInfo,
}) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            padding: '4px 4px 0px',
            alignItems: 'center',
            border: '1px solid lightgray',
            marginBottom: '.5rem',
        }}
        >
            {!!screeningCompoundInfoList && (
                <EditCompoundsButton
                    toolBarId={toolBarId}
                    compoundOptions={screeningCompoundInfoList}
                    updateScreeningCompoundInfo={updateScreeningCompoundInfo}
                    useColorOptions
                />
            )}
            {!!exportOptions && (
                <ExportButton toolBarId={toolBarId} exportOptions={exportOptions} />
            )}
            {!!charts && (
                <ChartButton toolBarId={toolBarId} charts={charts} setActiveChart={setChart} />
            )}
        </Box>
    );
}
export function GridToolBar({
    exportFileName, toolBarId, charts,
    setChart, useFilter, useEditCompoundsButton,
    updateScreeningCompoundInfo,
    availableScreeningCompoundInfoList, ...toolBarProps
}) {
    const fileName = exportFileName || `BMaps Table ${new Date().toLocaleDateString('en-US')}`;
    const ref = useGridApiContext();
    const exportOptions = [
        {
            title: 'Export All to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                allColumns: true,
                getRowsToExport: ({ apiRef }) => gridSortedRowIdsSelector(apiRef),
                fileName,
                includeColumnGroupsHeaders: false,
            }),
        },
        {
            title: 'Export Visible to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef),
                fileName,
                includeColumnGroupsHeaders: false,
            }),
        },
    ];
    return (
        <GridToolbarContainer sx={{ justifyContent: 'flex-start' }} {...toolBarProps}>
            {useEditCompoundsButton
                && (
                    <EditCompoundsButton
                        toolBarId={toolBarId}
                        compoundOptions={availableScreeningCompoundInfoList}
                        updateScreeningCompoundInfo={updateScreeningCompoundInfo}
                    />
                )}
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
            <ExportButton toolBarId={toolBarId} exportOptions={exportOptions} />
            {useFilter && <GridToolbarFilterButton />}
            {charts && charts.length > 1
                && (
                    <ChartButton toolBarId={toolBarId} charts={charts} setActiveChart={setChart} />
                )}
        </GridToolbarContainer>
    );
}

function ExportButton({ toolBarId, exportOptions }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);

    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );

    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<ExportIcon />}
                aria-describedby={`${toolBarId}-export`}
                onClick={handleClick}
            >
                Export
            </Button>
            <Menu
                id={`${toolBarId}-export`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {exportOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        onClick={() => {
                            handleClose();
                            option.handleExport();
                        }}
                    >
                        {option.title}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function ChartButton({ toolBarId, charts, setActiveChart }) {
    const [chartAnchorEl, setChartAnchorEl] = useState(null);

    const handleCharts = (event) => {
        setChartAnchorEl(event.currentTarget);
    };

    const handleChartClose = () => {
        setChartAnchorEl(null);
    };

    const openChart = Boolean(chartAnchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<BarChartIcon />}
                aria-describedby={`${toolBarId}-charts`}
                onClick={handleCharts}
            >
                Charts
            </Button>
            <Menu
                id={`${toolBarId}-charts`}
                anchorEl={chartAnchorEl}
                open={openChart}
                onClose={handleChartClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {charts.map((chart) => (
                    <MenuItem
                        key={chart.id}
                        onClick={() => { setActiveChart(chart.id); }}
                    >
                        {chart.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// used by the scoring coral toolbar
function AddCompoundButton({ compoundOptions, toolBarId }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                aria-describedby={`${toolBarId}-compound`}
                onClick={handleClick}
            >
                Compound
            </Button>
            <Menu
                id={`${toolBarId}-compound`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {compoundOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        onClick={() => {
                            handleClose();
                            option.onClick();
                        }}
                    >
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '3rem',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                        >
                            <span style={{ flex: 1 }}>{option.title}</span>

                            {!!option.smiles && (
                                <IndigoSvg
                                    smiles={option.smiles}
                                    style={{
                                        padding: '0',
                                        height: '100%',
                                        width: '100%',
                                    }}
                                    autoUpdate
                                    alt={`2D structure of ${option.title}`}
                                />
                            )}

                        </span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// used by the screening toolbar
function EditCompoundsButton({
    compoundOptions, toolBarId, updateScreeningCompoundInfo, useColorOptions,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [
        localScreeningCompoundInfoList,
        setLocalScreeningCompoundInfoList,
    ] = useState(compoundOptions);

    const handleClose = () => {
        updateScreeningCompoundInfo(localScreeningCompoundInfoList);
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<Edit />}
                aria-describedby={`${toolBarId}-compound`}
                onClick={handleClick}
            >
                Compounds
            </Button>
            <Popover
                id={`${toolBarId}-compound`}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            margin: '0.5rem 0.5rem 0 1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between',
                        }}
                    >
                        Configure compounds
                        <IconButton onClick={handleClose}><Close /></IconButton>
                    </Typography>
                    <ScreeningCompoundTable
                        screeningCompoundInfoList={localScreeningCompoundInfoList}
                        updateScreeningCompoundInfo={setLocalScreeningCompoundInfoList}
                        useColorOptions={useColorOptions}
                    />
                </Box>
            </Popover>
        </>
    );
}

/**
 *
 * @param {{
 * screeningCompoundInfoList: ScreeningCompoundInfo[],
 * updateScreeningCompoundInfo: () => {},
 * useColorOptions: boolean
 * }} param0
 * @returns
 */
function ScreeningCompoundTable({
    screeningCompoundInfoList, updateScreeningCompoundInfo,
    useColorOptions,
}) {
    if (!screeningCompoundInfoList || screeningCompoundInfoList.length === 0) return null;
    return (
        <>
            <TableContainer sx={{ margin: '0 0 0.5rem 0', overflow: 'unset' }}>
                <Table aria-label="Coral Compounds" size="small">
                    <TableHead />
                    <TableBody>
                        {screeningCompoundInfoList.map((screeningCompoundInfo, i) => (
                            <React.Fragment key={screeningCompoundInfo.smiles}>
                                {!!screeningCompoundInfo && (
                                    <ScreeningCompoundRow
                                        compoundIndex={i}
                                        screeningCompoundInfoList={screeningCompoundInfoList}
                                        updateScreeningCompoundInfo={
                                            updateScreeningCompoundInfo
                                        }
                                        useColorOptions={useColorOptions}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

function ScreeningCompoundRow({
    compoundIndex, screeningCompoundInfoList, updateScreeningCompoundInfo, useColorOptions,
}) {
    const [editingName, setEditingName] = useState(false);
    const colorPickerRef = useRef(null);

    // onChange event in the color picker is fired while the color picker is open
    // this can cause an update loop of the coral map being set over and over again.
    // To avoid this, we listen to the change event and only update color when picker is closed.
    useEffect(() => {
        if (!colorPickerRef?.current) return () => {};
        const el = colorPickerRef.current;

        const handleColorChange = (e) => {
            const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
            newScreeningCompoundInfoList[compoundIndex].baseColor = e.target.value;
            updateScreeningCompoundInfo(newScreeningCompoundInfoList);
        };
        el.addEventListener('change', handleColorChange);
        return () => {
            el.removeEventListener('change', handleColorChange);
        };
    }, []);

    const smiles = screeningCompoundInfoList[compoundIndex].smiles;
    return (
        <TableRow
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
            <TableCell align="left">
                <BMapsTooltip
                    title="Include this compound in the data"
                >
                    <Checkbox
                        checked={screeningCompoundInfoList[compoundIndex].active}
                        onChange={(event) => {
                            const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
                            newScreeningCompoundInfoList[compoundIndex]
                                .active = event.target.checked;
                            updateScreeningCompoundInfo(newScreeningCompoundInfoList);
                        }}
                    />
                </BMapsTooltip>
            </TableCell>
            <TableCell align="left">
                {!editingName
                    ? (
                        <>
                            <BMapsTooltip noMaxWidth title="Change compound name for data display">
                                <IconButton onClick={() => setEditingName(true)}>
                                    <Edit />
                                </IconButton>
                            </BMapsTooltip>
                            {' '}
                            {getNumberedCompoundName(
                                screeningCompoundInfoList[compoundIndex], screeningCompoundInfoList
                            )}
                        </>
                    ) : (
                        <EditInPlaceInput
                            value={screeningCompoundInfoList[compoundIndex].name}
                            setValue={(value) => {
                                const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
                                newScreeningCompoundInfoList[compoundIndex].name = value;
                                updateScreeningCompoundInfo(newScreeningCompoundInfoList);
                                setEditingName(false);
                            }}
                            stopEditing={() => setEditingName(false)}
                            Component={TextArea}
                        />
                    )}
            </TableCell>
            <TableCell align="left">
                <HoverableCompoundStructureCell
                    smiles={smiles}
                    uniqueId={smiles}
                    compoundName={screeningCompoundInfoList[compoundIndex].name}
                    imageStyles={{ height: '3.5rem', width: '10rem' }}
                    popoverImageStyles={{ height: '8rem' }}
                />
            </TableCell>
            <TableCell align="center">
                <BMapsTooltip
                    title={`Copy SMILES: ${smiles}`}
                    noMaxWidth
                >
                    <span>
                        {/* This span allows BMapsTooltip to work with CopyToClipboardButton */}
                        <CopyToClipboardButton
                            copyTarget={smiles}
                            tooltipMessage="Copied SMILES"
                            TooltipProps={{
                                placement: 'right',
                                arrow: true,
                                'aria-label': 'Copy SMILES',
                            }}
                            IconButtonProps={{ size: 'small' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <FileCopyIcon />
                                <span style={{ fontSize: 'small' }}>SMI</span>
                            </div>
                        </CopyToClipboardButton>
                    </span>
                </BMapsTooltip>
            </TableCell>
            {!!useColorOptions && (
                <TableCell align="right">
                    <input
                        ref={colorPickerRef}
                        type="color"
                        defaultValue={screeningCompoundInfoList[compoundIndex].baseColor}
                    />
                </TableCell>
            )}
        </TableRow>
    );
}
